import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContextType, AuthState, LoginCredentials, User } from '../types/auth';
import { authService } from '../services/authService';
import securityConfig from '../config/security';
import toast from 'react-hot-toast';

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem(securityConfig.TOKEN_STORAGE_KEY),
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check token validity on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem(securityConfig.TOKEN_STORAGE_KEY);
      if (token) {
        try {
          const isValid = await authService.verifyToken();
          if (isValid) {
            const user = await authService.getProfile();
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user, token },
            });
          } else {
            localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
            localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
            dispatch({ type: 'AUTH_LOGOUT' });
          }
        } catch (error) {
          localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
          localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Auto-logout when token expires
  useEffect(() => {
    const token = localStorage.getItem(securityConfig.TOKEN_STORAGE_KEY);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        
        if (expirationTime <= currentTime) {
          // Token has expired
          localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
    localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
          dispatch({ type: 'AUTH_LOGOUT' });
          toast.error(t('toast.sessionExpired'));
        } else {
          // Set timeout to logout when token expires
          const timeUntilExpiry = expirationTime - currentTime;
          const timeoutId = setTimeout(() => {
            localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
    localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
            dispatch({ type: 'AUTH_LOGOUT' });
            toast.error(t('toast.sessionExpired'));
          }, timeUntilExpiry);

          return () => clearTimeout(timeoutId);
        }
      } catch (error) {
        // Invalid token format
        localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
    localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
        dispatch({ type: 'AUTH_LOGOUT' });
        toast.error(t('toast.invalidSession'));
      }
    }
  }, []);

  const login = React.useCallback(async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.login(credentials);
      
      localStorage.setItem(securityConfig.TOKEN_STORAGE_KEY, response.access_token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user, token: response.access_token },
      });
      
      toast.success(t('toast.loginSuccess'));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('toast.loginFailed');
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
    }
  }, [dispatch]);

  const loginWithToken = React.useCallback(async (token: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // Store the token
      localStorage.setItem(securityConfig.TOKEN_STORAGE_KEY, token);
      
      // Verify token and get user info
      const user = await authService.getProfile();
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token },
      });
      
      return user;
    } catch (error: any) {
      // Clear invalid token
      localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
      localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
      
      const errorMessage = error.response?.data?.message || t('toast.tokenAuthFailed');
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw new Error(errorMessage);
    }
  }, [dispatch]);

  const logout = React.useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Continue with logout even if server request fails
    }
    localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
    localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
    dispatch({ type: 'AUTH_LOGOUT' });
    toast.success(t('toast.logoutSuccess'));
  }, [dispatch]);

  const clearError = React.useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, [dispatch]);

  const verifyToken = React.useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await authService.verifyToken();
      return isValid;
    } catch (error) {
      return false;
    }
  }, []);

  const value: AuthContextType = React.useMemo(() => ({
    ...state,
    login,
    loginWithToken,
    logout,
    clearError,
    verifyToken,
  }), [state, login, loginWithToken, logout, clearError, verifyToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};